import { todayDateRangeInUserTimeZone } from '@/helpers';
import { cloneDeep } from 'lodash-es';
import { BetSettlementItem } from '@/models/betSettlement';
import { createBetSettlementPageHeaders } from '@/pages/admin/betSettlement/BetSettlementPageHeaders';
import { isRequestCanceled } from '@/services/baseService';
import { betSettlementService } from '@/services/betSettlementService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { useFilterPersistence } from '@/stores/composables/useFilterPersistence';
import { IListParams, IPageOptions } from '@/types';
import { IBetSettlementFilters, IBetSettlementStats } from '@/types/betSettlement';
import type { DataTableHeader } from '@/types/vuetify';

const defaultSortBy = [{ key: 'created_at', order: 'desc' as const }];

function createBetSettlementStoreSetup(storeId: string) {
    // --- state ---
    const items = shallowRef<BetSettlementItem[]>([]);
    const total = ref<number>(0);
    const loading = ref(false);
    const stats = ref<IBetSettlementStats | undefined>(undefined);
    const loadGeneration = createLoadingGenerationTracker();

    // --- filters ---
    const filters = reactive<IBetSettlementFilters>({
        betSettlementDate: {
            value: todayDateRangeInUserTimeZone(),
            title: 'betSettlement.dateFilter',
            allowSaveState: false,
            disableClear: true,
        },
        betSettlementTypeId: {
            value: null,
            title: 'betSettlement.type',
            allowSaveState: true,
        },
        betBookmakerId: {
            value: null,
            title: 'bets.bookmaker',
            allowSaveState: true,
        },
        betAccountId: {
            value: null,
            title: 'bets.account',
            allowSaveState: true,
        },
        betInternalId: {
            value: null,
            title: 'betSettlement.betId',
            allowSaveState: false,
        },
        betRef: {
            value: null,
            title: 'betSettlement.bookBetId',
            allowSaveState: false,
        },
    });

    // --- filter panel ---
    const { hiddenFilters, visibleFiltersMobile, filterOrder, availableFilters, useButtonForFiltersApply, saveFiltersState, visibleFilters } =
        useFilterPanelState(filters);

    // --- options ---
    const options = ref<IPageOptions>({
        page: 1,
        itemsPerPage: 100,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [...defaultSortBy],
    });

    // --- reset ---
    function resetFilters() {
        filters.betSettlementDate.value = todayDateRangeInUserTimeZone();
        filters.betSettlementTypeId.value = null;
        filters.betBookmakerId.value = null;
        filters.betAccountId.value = null;
        filters.betInternalId.value = null;
        filters.betRef.value = null;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createBetSettlementPageHeaders());
    const availableColumns = computed(() => headers.value.filter((header) => header.title));
    const hiddenColumns = ref<string[]>([]);
    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title))
    );

    // --- persistence ---
    const { reloadFiltersFromStorage } = useFilterPersistence({
        storeId,
        primaryStoreId: 'betSettlement',
        filters,
        saveFiltersState,
        resetFilters,
    });

    // --- actions ---
    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const filtersToSend = cloneDeep(filters);
            if (filters.betInternalId.value || filters.betRef.value) {
                filtersToSend.betSettlementDate = { ...filtersToSend.betSettlementDate, value: null };
            }

            const params: IListParams<IBetSettlementFilters> = {
                filters: filtersToSend,
                options: {
                    sortBy: options.value.sortBy,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: storeId === 'betSettlement' ? ['Stats'] : [],
                requiredDictionaries: [],
                requestKey: `bet-settlement-list:${storeId}`,
            };

            const response = await betSettlementService.list(params);
            items.value = (response.data.rows ?? []).map((row) => new BetSettlementItem(row));
            total.value = response.data.total ?? 0;
            stats.value = response.data.stats;
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
        if (value.sortBy !== undefined) {
            options.value.sortBy = value.sortBy;
            loadData();
        }
    }

    function clearData() {
        items.value = [];
        total.value = 0;
        stats.value = undefined;
        options.value.page = 1;
        loading.value = false;
    }

    // --- return ---
    return {
        storeId,
        items,
        total,
        stats,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        options,
        loadData,
        updateOptions,
        clearData,
        hasRefresh: false,
    };
}

export function useBetSettlementStore(storeId: string = 'betSettlement') {
    const useStore = defineStore(storeId, () => createBetSettlementStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'betSettlement', defaultSortBy),
    });
    return useStore();
}
